import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Flame, Search as SearchIcon, ArrowUpDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { useInView } from "@/hooks/use-in-view";
import {
  FALLBACK, HUB_LABELS, resolveIcon,
  type TopHub, type TopRelatedItem,
} from "@/lib/top-related";

type SortMode = "rank" | "popularity" | "az" | "recent";

/**
 * Backwards-compatible props: `hub` is preferred; `context` still accepted.
 */
export function TopRelatedDigitalItems({
  hub,
  context,
  title = "Top 10 Related Digital Items",
  eyebrow = "Most Popular",
}: {
  hub?: TopHub;
  context?: TopHub;
  title?: string;
  eyebrow?: string;
}) {
  const activeHub: TopHub = (hub ?? context ?? "services");

  const { data: items = FALLBACK[activeHub] } = useQuery<TopRelatedItem[]>({
    queryKey: ["top-related", activeHub],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("top_related_items")
        .select("*")
        .eq("hub", activeHub)
        .eq("active", true)
        .order("rank");
      if (error || !data || data.length === 0) return FALLBACK[activeHub];
      return data as TopRelatedItem[];
    },
    initialData: FALLBACK[activeHub],
    staleTime: 5 * 60_000,
  });

  const [sort, setSort] = useState<SortMode>("rank");
  const [category, setCategory] = useState<string>("all");
  const [q, setQ] = useState("");

  const categories = useMemo(
    () => Array.from(new Set(items.map((i) => i.category).filter(Boolean))).sort(),
    [items],
  );

  const visible = useMemo(() => {
    let arr = items.slice();
    if (category !== "all") arr = arr.filter((i) => i.category === category);
    const needle = q.trim().toLowerCase();
    if (needle) arr = arr.filter((i) =>
      i.title.toLowerCase().includes(needle) ||
      i.description.toLowerCase().includes(needle) ||
      i.tag.toLowerCase().includes(needle),
    );
    arr.sort((a, b) => {
      switch (sort) {
        case "popularity": return b.popularity - a.popularity;
        case "az": return a.title.localeCompare(b.title);
        case "recent":
          return (b.created_at ?? "").localeCompare(a.created_at ?? "");
        default: return a.rank - b.rank;
      }
    });
    return arr;
  }, [items, sort, category, q]);

  return (
    <section className="py-16">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
              <Flame className="h-3.5 w-3.5" />
              {eyebrow} · {HUB_LABELS[activeHub]}
            </div>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              The ten most-requested digital services and products our clients ship this quarter — ranked and ready to explore.
            </p>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="h-9 w-40 rounded-md border border-border bg-background pl-7 pr-2 text-sm outline-none focus:border-primary/50"
              />
            </div>
            <label className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 h-9 text-sm">
              <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortMode)}
                className="bg-transparent text-sm outline-none"
                aria-label="Sort"
              >
                <option value="rank">Rank</option>
                <option value="popularity">Popularity</option>
                <option value="az">A → Z</option>
                <option value="recent">Recently added</option>
              </select>
            </label>
          </div>
        </div>

        {/* Category chips */}
        {categories.length > 1 && (
          <div className="mb-6 flex flex-wrap gap-2">
            <Chip active={category === "all"} onClick={() => setCategory("all")}>All</Chip>
            {categories.map((c) => (
              <Chip key={c} active={category === c} onClick={() => setCategory(c)}>
                {c}
              </Chip>
            ))}
          </div>
        )}

        {visible.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No items match your filters.
          </p>
        ) : (
          <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
            {visible.map((it) => (
              <TopItemCard key={`${it.hub}-${it.rank}-${it.title}`} item={it} hub={activeHub} />
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

function Chip({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded-full border px-3 py-1 text-xs font-medium transition " +
        (active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground")
      }
    >
      {children}
    </button>
  );
}

function TopItemCard({ item, hub }: { item: TopRelatedItem; hub: TopHub }) {
  const Icon = resolveIcon(item.icon);
  const { ref, inView } = useInView<HTMLLIElement>({ once: true, threshold: 0.4 });
  const impressed = useRef(false);

  useEffect(() => {
    if (!inView || impressed.current) return;
    impressed.current = true;
    trackEvent("top_related_impression", {
      hub, rank: item.rank, item_id: item.id, to: item.to_path, title: item.title, category: item.category,
    });
  }, [inView, hub, item]);

  return (
    <li ref={ref}>
      <Link
        to={item.to_path as never}
        onClick={() =>
          trackEvent("top_related_click", {
            hub, rank: item.rank, item_id: item.id, to: item.to_path, title: item.title, category: item.category,
          })
        }
        className="group flex items-start gap-4 rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-card"
      >
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-lg font-bold text-primary">
          {String(item.rank).padStart(2, "0")}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-primary" />
            <h3 className="truncate text-base font-semibold">{item.title}</h3>
            {item.tag && (
              <span className="ml-auto hidden sm:inline-flex rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {item.tag}
              </span>
            )}
          </div>
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.description}</p>
          <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary opacity-0 transition group-hover:opacity-100">
            Explore <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </Link>
    </li>
  );
}
